<template>
  <div class="tt-root">
    <!-- 共用头部导航栏 -->
    <header-nav></header-nav>

    <!-- ============ 布局：左侧可收缩游戏菜单 + 主内容区 ============ -->
    <div class="tt-layout">
      <!-- 左侧侧边栏（可收缩隐藏） -->
      <aside class="tt-side" :class="{ collapsed: sidebarCollapsed }">
        <!-- 收缩/展开按钮 -->
        <button
            type="button"
            class="tt-collapse-btn"
            :title="sidebarCollapsed ? '展开菜单' : '收起菜单'"
            @click="sidebarCollapsed = !sidebarCollapsed">
          <span class="tt-collapse-arrow">{{ sidebarCollapsed ? '▶' : '◀' }}</span>
        </button>

        <!-- 上部：标题 + 账号信息 -->
        <div class="tt-side-top">
          <div class="tt-brand">
            <span class="tt-brand-icon">🧸</span>
            <span v-show="!sidebarCollapsed" class="tt-brand-name">潮玩天地</span>
          </div>
          <div class="tt-user">
            <div class="tt-avatar">
              <img v-if="userInfo.avatar" class="tt-avatar-img" :src="userInfo.avatar" alt="avatar" />
              <span v-else class="tt-avatar-text">{{ userInfo.name ? userInfo.name.charAt(0) : '游' }}</span>
            </div>
            <div v-show="!sidebarCollapsed" class="tt-user-info">
              <span class="tt-user-name">{{ userInfo.name || '游客' }}</span>
              <span class="tt-user-no">{{ userInfo.no || '登录后解锁更多' }}</span>
            </div>
          </div>

          <!-- 星裙币小卡片 -->
          <div class="tt-coin-card" :title="'星裙币 × ' + (userInfo.coin || 0)">
            <span class="tt-coin-ico">🪙</span>
            <span v-show="!sidebarCollapsed" class="tt-coin-label">星裙币</span>
            <span class="tt-coin-num">{{ userInfo.coin || 0 }}</span>
          </div>
        </div>

        <!-- 中部：游戏选项菜单 -->
        <nav class="tt-side-mid">
          <button
              v-for="g in games"
              :key="g.id"
              class="tt-menu-item"
              :class="{ active: activeGame === g.id }"
              :title="g.name"
              @click="switchGame(g.id)">
            <span class="tt-menu-ico">{{ g.icon }}</span>
            <span v-show="!sidebarCollapsed" class="tt-menu-name">{{ g.name }}</span>
          </button>
        </nav>

        <!-- 下部：页脚 · 设置（暂为占位图标） -->
        <div class="tt-side-foot">
          <button type="button" class="tt-settings-btn" title="设置" @click="openSettings">
            <span class="tt-settings-ico">⚙️</span>
            <span v-show="!sidebarCollapsed" class="tt-settings-name">设置</span>
          </button>
        </div>
      </aside>

      <!-- 主内容区：游戏面板 -->
      <main class="tt-main">

    <!-- ================================================= -->
    <!-- ① 猜数字                                            -->
    <!-- ================================================= -->
    <section v-show="activeGame === 'guess'" class="mg-panel">
      <div class="mg-panel-head">
        <span class="mg-panel-ico">🔢</span>
        <div>
          <h2 class="mg-panel-title">猜数字</h2>
          <p class="mg-panel-sub">我心里想了一个数字，你来猜猜看呀～</p>
        </div>
      </div>

      <div class="gs-levels">
        <button
          v-for="lv in gsLevels"
          :key="lv.id"
          class="gs-level"
          :class="{ active: gsLevel === lv.id }"
          @click="startGuess(lv.id)"
        >
          <span class="gs-level-name">{{ lv.name }}</span>
          <span class="gs-level-desc">1 ~ {{ lv.max }} · {{ lv.tries }} 次机会</span>
        </button>
      </div>

      <div class="gs-stage">
        <div class="gs-range">
          范围 <b>{{ gsMin }}</b> ~ <b>{{ gsMax }}</b>
          <span class="gs-rest" :class="{ warn: gsRest <= 3 }">
            还剩 {{ gsRest }} 次机会
          </span>
        </div>

        <div v-if="gsStatus === 'playing'" class="gs-form">
          <input
            v-model.trim="gsInput"
            class="gs-input"
            type="number"
            :min="gsMin"
            :max="gsMax"
            placeholder="输入你的猜测"
            @keyup.enter="submitGuess"
          />
          <button class="mg-btn mg-btn-pink" :disabled="!gsInput" @click="submitGuess">
            猜一猜
          </button>
        </div>

        <div v-else-if="gsStatus === 'win'" class="gs-result win">
          <div class="gs-result-emoji">🎉</div>
          <p class="gs-result-title">太棒啦！猜中啦！</p>
          <p class="gs-result-info">答案就是 <b>{{ gsTarget }}</b>，你用了 {{ gsTries }} 次猜中～</p>
        </div>

        <div v-else class="gs-result lose">
          <div class="gs-result-emoji">😿</div>
          <p class="gs-result-title">哎呀，机会用完啦</p>
          <p class="gs-result-info">答案是 <b>{{ gsTarget }}</b>，再试一次吧！</p>
        </div>

        <button
          v-if="gsStatus !== 'playing'"
          class="mg-btn mg-btn-pink mg-btn-block"
          @click="startGuess(gsLevel)"
        >
          再来一局
        </button>

        <div v-if="gsHistory.length" class="gs-history">
          <p class="gs-history-title">📜 猜测记录</p>
          <ul class="gs-history-list">
            <li
              v-for="(h, i) in gsHistory"
              :key="i"
              class="gs-history-item"
              :class="'hint-' + h.hint"
            >
              <span class="gs-h-num">第 {{ i + 1 }} 次 · {{ h.value }}</span>
              <span class="gs-h-tip">
                {{ h.hint === 'high' ? '↑ 大了' : h.hint === 'low' ? '↓ 小了' : '✓ 猜中' }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- ================================================= -->
    <!-- ② 石头剪刀布                                        -->
    <!-- ================================================= -->
    <section v-show="activeGame === 'rps'" class="mg-panel">
      <div class="mg-panel-head">
        <span class="mg-panel-ico">✊</span>
        <div>
          <h2 class="mg-panel-title">石头剪刀布</h2>
          <p class="mg-panel-sub">和星裙小助手一决高下吧！</p>
        </div>
      </div>

      <div class="rps-score">
        <div class="rps-score-box">
          <span class="rps-score-num pink">{{ rpsWin }}</span>
          <span class="rps-score-label">我赢</span>
        </div>
        <div class="rps-score-box">
          <span class="rps-score-num gray">{{ rpsDraw }}</span>
          <span class="rps-score-label">平局</span>
        </div>
        <div class="rps-score-box">
          <span class="rps-score-num blue">{{ rpsLose }}</span>
          <span class="rps-score-label">我输</span>
        </div>
      </div>

      <div class="rps-arena">
        <div class="rps-side">
          <p class="rps-side-label">我</p>
          <div class="rps-gesture" :class="{ playing: rpsAnimating }">
            <span v-if="rpsPlayer" class="rps-emoji">{{ rpsPlayer.icon }}</span>
            <span v-else class="rps-emoji rps-empty">❓</span>
          </div>
        </div>
        <div class="rps-vs">
          <span v-if="rpsAnimating" class="rps-vs-text">VS</span>
          <span v-else-if="rpsResult === 'win'" class="rps-vs-text win">🎉 赢啦</span>
          <span v-else-if="rpsResult === 'lose'" class="rps-vs-text lose">😢 输啦</span>
          <span v-else-if="rpsResult === 'draw'" class="rps-vs-text draw">🤝 平局</span>
          <span v-else class="rps-vs-text">VS</span>
        </div>
        <div class="rps-side">
          <p class="rps-side-label">小助手</p>
          <div class="rps-gesture" :class="{ playing: rpsAnimating }">
            <span v-if="rpsCpu" class="rps-emoji">{{ rpsCpu.icon }}</span>
            <span v-else class="rps-emoji rps-empty">❓</span>
          </div>
        </div>
      </div>

      <div class="rps-choices">
        <button
          v-for="c in rpsChoices"
          :key="c.id"
          class="rps-choice"
          :disabled="rpsAnimating"
          @click="playRps(c)"
        >
          <span class="rps-choice-emoji">{{ c.icon }}</span>
          <span class="rps-choice-name">{{ c.name }}</span>
        </button>
      </div>
    </section>

    <!-- ================================================= -->
    <!-- ③ 记忆翻牌                                         -->
    <!-- ================================================= -->
    <section v-show="activeGame === 'memory'" class="mg-panel">
      <div class="mg-panel-head">
        <span class="mg-panel-ico">🧠</span>
        <div>
          <h2 class="mg-panel-title">记忆翻牌</h2>
          <p class="mg-panel-sub">翻开卡片，找出所有相同的图案吧！</p>
        </div>
      </div>

      <div class="mem-bar">
        <span class="mem-info">⏱ {{ memSeconds }} 秒</span>
        <span class="mem-info">👆 {{ memMoves }} 步</span>
        <span class="mem-info">💗 已配对 {{ memMatchedCount }} / {{ memPairTotal }}</span>
        <button class="mg-btn mg-btn-pink mg-btn-sm" @click="startMemory">重新洗牌</button>
      </div>

      <div v-if="memDone" class="mem-done">
        <span class="mem-done-emoji">🌟</span>
        <p class="mem-done-text">全部配对成功！用了 {{ memSeconds }} 秒、{{ memMoves }} 步～</p>
      </div>

      <div class="mem-grid">
        <button
          v-for="(c, i) in memCards"
          :key="i"
          class="mem-card"
          :class="{
            flipped: c.flipped || c.matched,
            matched: c.matched
          }"
          :disabled="c.matched || memLock"
          @click="flipCard(i)"
        >
          <span class="mem-card-back">🎀</span>
          <span class="mem-card-front">{{ c.icon }}</span>
        </button>
      </div>
    </section>

    <!-- ================================================= -->
    <!-- ④ 井字棋                                           -->
    <!-- ================================================= -->
    <section v-show="activeGame === 'ttt'" class="mg-panel">
      <div class="mg-panel-head">
        <span class="mg-panel-ico">⭕</span>
        <div>
          <h2 class="mg-panel-title">井字棋</h2>
          <p class="mg-panel-sub">三子连线即为胜利，和 AI 过过招吧！</p>
        </div>
      </div>

      <div class="ttt-bar">
        <button
          class="ttt-mode"
          :class="{ active: tttMode === 'ai' }"
          @click="startTtt('ai')"
        >🤖 人机对战</button>
        <button
          class="ttt-mode"
          :class="{ active: tttMode === 'pvp' }"
          @click="startTtt('pvp')"
        >👭 双人对战</button>
      </div>

      <div class="ttt-status">
        <template v-if="tttOver">
          <span v-if="tttWinner === 'X'" class="ttt-status-win pink">🌸 X 方获胜！</span>
          <span v-else-if="tttWinner === 'O'" class="ttt-status-win blue">🌊 O 方获胜！</span>
          <span v-else class="ttt-status-draw">🤝 平局！</span>
          <button class="mg-btn mg-btn-pink mg-btn-sm" @click="startTtt(tttMode)">再来一局</button>
        </template>
        <template v-else>
          <span class="ttt-turn" :class="{ pink: tttTurn === 'X', blue: tttTurn === 'O' }">
            轮到 {{ tttTurn === 'X' ? '🌸 X' : '🌊 O' }}
          </span>
          <button class="mg-btn mg-btn-pink mg-btn-sm" @click="startTtt(tttMode)">重新开始</button>
        </template>
      </div>

      <div class="ttt-board">
        <button
          v-for="(cell, i) in tttBoard"
          :key="i"
          class="ttt-cell"
          :class="{ win: tttWinLine && tttWinLine.includes(i) }"
          :disabled="!!cell || tttOver || (tttMode === 'ai' && tttTurn === 'O')"
          @click="tttMove(i)"
        >
          <span v-if="cell" :class="cell === 'X' ? 'cell-x' : 'cell-o'">{{ cell }}</span>
        </button>
      </div>

      <div class="ttt-score">
        <span class="ttt-score-item">🌸 X：{{ tttScoreX }} 胜</span>
        <span class="ttt-score-item">🤝 平：{{ tttDraws }}</span>
        <span class="ttt-score-item">🌊 O：{{ tttScoreO }} 胜</span>
      </div>
    </section>

    <!-- ================================================= -->
    <!-- ⑤ 扫雷                                            -->
    <!-- ================================================= -->
    <section v-show="activeGame === 'mines'" class="mg-panel">
      <div class="mg-panel-head">
        <span class="mg-panel-ico">💣</span>
        <div>
          <h2 class="mg-panel-title">扫雷</h2>
          <p class="mg-panel-sub">9×9 雷区，共 10 颗雷，小心别踩到啦！</p>
        </div>
      </div>

      <div class="ms-bar">
        <div class="ms-mode">
          <button
            class="ms-mode-btn"
            :class="{ active: msMode === 'reveal' }"
            @click="msMode = 'reveal'"
          >🖐 翻开</button>
          <button
            class="ms-mode-btn"
            :class="{ active: msMode === 'flag' }"
            @click="msMode = 'flag'"
          >🚩 插旗</button>
        </div>
        <span class="ms-info">💣 {{ 10 - msFlagCount }}</span>
        <span class="ms-info">⏱ {{ msSeconds }} 秒</span>
        <button class="mg-btn mg-btn-pink mg-btn-sm" @click="startMines">重新开始</button>
      </div>

      <div v-if="msStatus !== 'playing'" class="ms-overlay" @click="startMines">
        <template v-if="msStatus === 'win'">
          <span class="ms-overlay-emoji">🎉</span>
          <p class="ms-overlay-text">太厉害啦！排完了所有雷！</p>
        </template>
        <template v-else>
          <span class="ms-overlay-emoji">💥</span>
          <p class="ms-overlay-text">踩到雷啦… 点击重新开始</p>
        </template>
      </div>

      <div class="ms-board">
        <button
          v-for="(cell, i) in msBoard"
          :key="i"
          class="ms-cell"
          :class="{
            revealed: cell.revealed,
            'is-mine': cell.revealed && cell.mine,
            boom: cell.revealed && cell.mine && msBoomIndex === i
          }"
          @click="msCellClick(i)"
        >
          <template v-if="cell.revealed">
            <span v-if="cell.mine" class="ms-ico">💣</span>
            <span
              v-else-if="cell.adjacent"
              class="ms-num"
              :class="'n' + cell.adjacent"
            >{{ cell.adjacent }}</span>
          </template>
          <span v-else-if="cell.flagged" class="ms-ico">🚩</span>
          <span v-else class="ms-blank"></span>
        </button>
      </div>
      </section>

    <!-- ================================================= -->
    <!-- ⑥ 糖果2048                                           -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'g2048'" class="mg-panel">
      <Game2048></Game2048>
    </section>

    <!-- ================================================= -->
    <!-- ⑦ 夹娃娃机                                           -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'claw'" class="mg-panel">
      <ClawMachine></ClawMachine>
    </section>

    <!-- ================================================= -->
    <!-- ⑧ 俄罗斯方块（y7x7 原版）                             -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'tetris'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/tetris/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑨ 找不同（y7x7 原版，30 关）                         -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'finddiff'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/finddiff/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑩ 数字华容道（y7x7 原版）                          -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'hrd'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/huarongdao/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑪ 小球分类（y7x7 原版，30 关）                     -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'ballsort'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/ballsort/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑫ 推花盆（y7x7 原版，30 关）                       -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'pushflowerpot'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/pushflowerpot/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑬ 解救方块（y7x7 原版，30 关）                     -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'unblockmepack'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/unblockmepack/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑭ 开心消消乐（y7x7 原版，30 关）                   -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'happymatch'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/happymatch/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑯ 填满一笔画（y7x7 原版）                          -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'trace'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/trace/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑰ 禅意五子棋（y7x7 原版）                          -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'wuziqi'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/wuziqi/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑱ 运气世界                                           -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'luck'" class="mg-panel">
      <LuckWorld></LuckWorld>
    </section>

    <!-- ================================================= -->
    <!-- ⑲ 帮我生长（y7x7 原版）                             -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'letmegrowup'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/letmegrowup/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ⑳ 甜点合并（y7x7 原版）                             -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'sweetdream'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/sweetdream/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ㉑ 经典迷宫（y7x7 原版，经典 100 关）               -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'maze'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/maze/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>

    <!-- ================================================= -->
    <!-- ㉒ 登山赛车（Fingersoft Lite，去广告与 SDK）       -->
    <!-- ================================================= -->
    <section v-if="activeGame === 'hillclimb'" class="mg-panel mg-panel-iframe">
      <iframe class="mg-game-frame" src="/minigames/hillclimb/index.html" frameborder="0" allow="autoplay; fullscreen"></iframe>
    </section>
      </main>
    </div>
  </div>
</template>

<script>
/* =====================================================
 * 潮玩天地 TrendToys（原 MiniGames 改造）
 * 左侧可收缩游戏菜单 + 主内容区
 * 游戏：猜数字 / 石头剪刀布 / 记忆翻牌 / 井字棋 / 扫雷
 *       / 糖果2048 / 夹娃娃机 / 俄罗斯方块 / 找不同
 *       / 数字华容道 / 小球分类 / 开心消消乐 / 运气世界
 * ===================================================== */
import HeaderNav from '../HeaderNav.vue'
import bus from '../../utils/bus'
import Game2048 from './Game2048.vue'
import ClawMachine from './ClawMachine.vue'
import LuckWorld from './LuckWorld.vue'

const RPS_CHOICES = [
  { id: 'rock', name: '石头', icon: '✊', beats: 'scissors' },
  { id: 'scissors', name: '剪刀', icon: '✌️', beats: 'paper' },
  { id: 'paper', name: '布', icon: '🖐️', beats: 'rock' }
]

const MEM_ICONS = ['🌸', '🍓', '🎀', '🍬', '🧁', '🌈', '💗', '🍭']
const TTT_WIN_LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6]
]

export default {
  name: 'TrendToys',
  components: { HeaderNav, Game2048, ClawMachine, LuckWorld },
  data () {
    return {
      /* ---------- 侧边栏 ---------- */
      sidebarCollapsed: false,
      userInfo: {},

      /* ---------- 通用 ---------- */
      activeGame: 'guess',
      games: [
        { id: 'guess', name: '猜数字', icon: '🔢' },
        { id: 'rps', name: '猜拳', icon: '✊' },
        { id: 'memory', name: '记忆翻牌', icon: '🧠' },
        { id: 'ttt', name: '井字棋', icon: '⭕' },
        { id: 'mines', name: '扫雷', icon: '💣' },
        { id: 'g2048', name: '糖果2048', icon: '🍬' },
        { id: 'claw', name: '夹娃娃机', icon: '🧸' },
        { id: 'tetris', name: '俄罗斯方块', icon: '🧩' },
        { id: 'finddiff', name: '找不同', icon: '🔍' },
        { id: 'hrd', name: '数字华容道', icon: '🀄' },
        { id: 'ballsort', name: '小球分类', icon: '🧪' },
        { id: 'pushflowerpot', name: '推花盆', icon: '🪴' },
        { id: 'unblockmepack', name: '解救方块', icon: '🧱' },
        { id: 'happymatch', name: '开心消消乐', icon: '🍭' },
        { id: 'trace', name: '填满一笔画', icon: '✏️' },
        { id: 'wuziqi', name: '禅意五子棋', icon: '⚫' },
        { id: 'luck', name: '运气世界', icon: '🎲' },
        { id: 'letmegrowup', name: '帮我生长', icon: '🌱' },
        { id: 'sweetdream', name: '甜点合并', icon: '🍰' },
        { id: 'maze', name: '经典迷宫', icon: '🌀' },
        { id: 'hillclimb', name: '登山赛车', icon: '🏔️' }
      ],

      /* ---------- 猜数字 ---------- */
      gsLevels: [
        { id: 'easy', name: '简单', max: 50, tries: 8 },
        { id: 'normal', name: '普通', max: 100, tries: 10 },
        { id: 'hard', name: '困难', max: 200, tries: 12 }
      ],
      gsLevel: 'normal',
      gsTarget: 0,
      gsMin: 1,
      gsMax: 100,
      gsTries: 0,
      gsRest: 10,
      gsInput: '',
      gsHistory: [],
      gsStatus: 'playing', // playing | win | lose

      /* ---------- 石头剪刀布 ---------- */
      rpsChoices: RPS_CHOICES,
      rpsPlayer: null,
      rpsCpu: null,
      rpsResult: '', // '' | win | lose | draw
      rpsWin: 0,
      rpsLose: 0,
      rpsDraw: 0,
      rpsAnimating: false,

      /* ---------- 记忆翻牌 ---------- */
      memCards: [],
      memFlipped: [],
      memMoves: 0,
      memSeconds: 0,
      memTimer: null,
      memDone: false,
      memLock: false,

      /* ---------- 井字棋 ---------- */
      tttBoard: Array(9).fill(''),
      tttTurn: 'X',
      tttOver: false,
      tttWinner: '',
      tttWinLine: null,
      tttMode: 'ai',
      tttScoreX: 0,
      tttScoreO: 0,
      tttDraws: 0,

      /* ---------- 扫雷 ---------- */
      msBoard: [],
      msMode: 'reveal',
      msStatus: 'playing', // playing | win | lose
      msFlagCount: 0,
      msSeconds: 0,
      msTimer: null,
      msBoomIndex: -1,
      msRows: 9,
      msCols: 9,
      msMineTotal: 10
    }
  },

  computed: {
    /* 记忆翻牌：已配对对数 */
    memPairTotal () {
      return MEM_ICONS.length
    },
    memMatchedCount () {
      return this.memCards.filter(c => c.matched).length / 2
    }
  },

  created () {
    this.loadUserInfo()
    // 登录/资料更新后即时刷新账号信息（头像/星裙币等）
    bus.$on('user-changed', this.handleUserChanged)
    this.startGuess('normal')
    this.startMemory()
    this.startTtt('ai')
    this.startMines()
  },

  beforeDestroy () {
    bus.$off('user-changed', this.handleUserChanged)
    if (this.memTimer) { clearInterval(this.memTimer); this.memTimer = null }
    if (this.msTimer) { clearInterval(this.msTimer); this.msTimer = null }
  },

  methods: {
    /* ================= 侧边栏 ================= */
    loadUserInfo () {
      try {
        const saved = sessionStorage.getItem('CurUser')
        if (saved) {
          this.userInfo = JSON.parse(saved)
        }
      } catch (e) {
        this.userInfo = {}
      }
    },
    // 登录/资料更新广播：即时刷新账号信息
    handleUserChanged (user) {
      this.userInfo = user || {}
    },
    openSettings () {
      this.$message && this.$message.info('⚙️ 设置功能开发中，敬请期待～')
    },

    /* ================= 通用 ================= */
    switchGame (id) {
      this.activeGame = id
    },

    /* ================= 猜数字 ================= */
    startGuess (levelId) {
      const lv = this.gsLevels.find(l => l.id === levelId) || this.gsLevels[1]
      this.gsLevel = lv.id
      this.gsMin = 1
      this.gsMax = lv.max
      this.gsTarget = this.randInt(1, lv.max)
      this.gsTries = 0
      this.gsRest = lv.tries
      this.gsInput = ''
      this.gsHistory = []
      this.gsStatus = 'playing'
    },
    submitGuess () {
      if (this.gsStatus !== 'playing') { return }
      const val = parseInt(this.gsInput, 10)
      if (isNaN(val) || val < this.gsMin || val > this.gsMax) {
        this.$message && this.$message.warning(`请输入 ${this.gsMin} ~ ${this.gsMax} 之间的数字`)
        return
      }
      this.gsTries++
      this.gsRest--
      let hint = ''
      if (val === this.gsTarget) {
        hint = 'hit'
        this.gsStatus = 'win'
      } else if (val > this.gsTarget) {
        hint = 'high'
        if (this.gsRest <= 0) { this.gsStatus = 'lose' }
      } else {
        hint = 'low'
        if (this.gsRest <= 0) { this.gsStatus = 'lose' }
      }
      this.gsHistory.push({ value: val, hint })
      this.gsInput = ''
      if (this.gsStatus === 'win' && this.$message) {
        this.$message.success('恭喜猜中！')
      }
    },

    /* ================= 石头剪刀布 ================= */
    playRps (playerChoice) {
      if (this.rpsAnimating) { return }
      const cpuChoice = RPS_CHOICES[this.randInt(0, 2)]
      this.rpsPlayer = playerChoice
      this.rpsCpu = cpuChoice
      this.rpsResult = ''
      this.rpsAnimating = true
      setTimeout(() => {
        if (playerChoice.id === cpuChoice.id) {
          this.rpsResult = 'draw'
          this.rpsDraw++
        } else if (playerChoice.beats === cpuChoice.id) {
          this.rpsResult = 'win'
          this.rpsWin++
        } else {
          this.rpsResult = 'lose'
          this.rpsLose++
        }
        this.rpsAnimating = false
      }, 600)
    },

    /* ================= 记忆翻牌 ================= */
    startMemory () {
      if (this.memTimer) { clearInterval(this.memTimer); this.memTimer = null }
      const deck = MEM_ICONS.concat(MEM_ICONS).map(icon => ({
        icon,
        flipped: false,
        matched: false
      }))
      // Fisher-Yates 洗牌
      for (let i = deck.length - 1; i > 0; i--) {
        const j = this.randInt(0, i)
        ;[deck[i], deck[j]] = [deck[j], deck[i]]
      }
      this.memCards = deck
      this.memFlipped = []
      this.memMoves = 0
      this.memSeconds = 0
      this.memDone = false
      this.memLock = false
      this.memTimer = setInterval(() => {
        if (!this.memDone) { this.memSeconds++ }
      }, 1000)
    },
    flipCard (index) {
      const card = this.memCards[index]
      if (this.memLock || card.matched || card.flipped) { return }
      card.flipped = true
      this.memFlipped.push(index)
      if (this.memFlipped.length === 2) {
        this.memMoves++
        this.memLock = true
        const [a, b] = this.memFlipped
        const cardA = this.memCards[a]
        const cardB = this.memCards[b]
        if (cardA.icon === cardB.icon) {
          cardA.matched = true
          cardB.matched = true
          this.memFlipped = []
          this.memLock = false
          if (this.memCards.every(c => c.matched)) {
            this.memDone = true
            if (this.memTimer) { clearInterval(this.memTimer); this.memTimer = null }
          }
        } else {
          setTimeout(() => {
            cardA.flipped = false
            cardB.flipped = false
            this.memFlipped = []
            this.memLock = false
          }, 750)
        }
      }
    },

    /* ================= 井字棋 ================= */
    startTtt (mode) {
      this.tttMode = mode
      this.tttBoard = Array(9).fill('')
      this.tttTurn = 'X'
      this.tttOver = false
      this.tttWinner = ''
      this.tttWinLine = null
    },
    tttMove (index) {
      if (this.tttOver || this.tttBoard[index]) { return }
      this.$set(this.tttBoard, index, this.tttTurn)
      if (this.checkTttWin()) {
        this.tttOver = true
        if (this.tttWinner === 'X') { this.tttScoreX++ } else { this.tttScoreO++ }
        return
      }
      if (this.tttBoard.every(c => c)) {
        this.tttOver = true
        this.tttWinner = ''
        this.tttDraws++
        return
      }
      this.tttTurn = this.tttTurn === 'X' ? 'O' : 'X'
      if (this.tttMode === 'ai' && this.tttTurn === 'O' && !this.tttOver) {
        this.$nextTick(() => {
          setTimeout(() => this.tttAiMove(), 350)
        })
      }
    },
    checkTttWin () {
      for (const line of TTT_WIN_LINES) {
        const [a, b, c] = line
        if (this.tttBoard[a] && this.tttBoard[a] === this.tttBoard[b] && this.tttBoard[a] === this.tttBoard[c]) {
          this.tttWinner = this.tttBoard[a]
          this.tttWinLine = line
          return true
        }
      }
      return false
    },
    tttAiMove () {
      const board = this.tttBoard.slice()
      const empty = board.map((v, i) => v ? -1 : i).filter(i => i >= 0)
      if (!empty.length || this.tttOver) { return }
      // 1. 自己能赢
      const winMove = this.findTttWinMove(board, 'O')
      if (winMove !== -1) {
        this.tttMove(winMove)
        return
      }
      // 2. 堵玩家
      const blockMove = this.findTttWinMove(board, 'X')
      if (blockMove !== -1) {
        this.tttMove(blockMove)
        return
      }
      // 3. 中心
      if (!board[4]) {
        this.tttMove(4)
        return
      }
      // 4. 随机角
      const corners = [0, 2, 6, 8].filter(i => !board[i])
      if (corners.length) {
        this.tttMove(corners[this.randInt(0, corners.length - 1)])
        return
      }
      // 5. 随机空位
      this.tttMove(empty[this.randInt(0, empty.length - 1)])
    },
    findTttWinMove (board, player) {
      for (let i = 0; i < 9; i++) {
        if (!board[i]) {
          const tmp = board.slice()
          tmp[i] = player
          for (const line of TTT_WIN_LINES) {
            const [a, b, c] = line
            if (tmp[a] && tmp[a] === tmp[b] && tmp[a] === tmp[c]) {
              return i
            }
          }
        }
      }
      return -1
    },

    /* ================= 扫雷 ================= */
    startMines () {
      if (this.msTimer) { clearInterval(this.msTimer); this.msTimer = null }
      const total = this.msRows * this.msCols
      this.msBoard = Array.from({ length: total }, () => ({
        mine: false,
        adjacent: 0,
        revealed: false,
        flagged: false
      }))
      // 布雷（第一次点击处排除）
      let planted = 0
      while (planted < this.msMineTotal) {
        const idx = this.randInt(0, total - 1)
        if (!this.msBoard[idx].mine) {
          this.msBoard[idx].mine = true
          planted++
        }
      }
      // 计算相邻雷数
      for (let i = 0; i < total; i++) {
        if (this.msBoard[i].mine) { continue }
        this.msBoard[i].adjacent = this.msNeighbors(i).filter(n => this.msBoard[n].mine).length
      }
      this.msMode = 'reveal'
      this.msStatus = 'playing'
      this.msFlagCount = 0
      this.msSeconds = 0
      this.msBoomIndex = -1
      this.msTimer = setInterval(() => {
        if (this.msStatus === 'playing') { this.msSeconds++ }
      }, 1000)
    },
    msNeighbors (idx) {
      const { msCols: cols, msRows: rows } = this
      const r = Math.floor(idx / cols)
      const c = idx % cols
      const res = []
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) { continue }
          const nr = r + dr
          const nc = c + dc
          if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) {
            res.push(nr * cols + nc)
          }
        }
      }
      return res
    },
    msCellClick (idx) {
      if (this.msStatus !== 'playing') { return }
      const cell = this.msBoard[idx]
      if (cell.revealed) { return }
      if (this.msMode === 'flag') {
        if (cell.flagged) {
          cell.flagged = false
          this.msFlagCount--
        } else {
          cell.flagged = true
          this.msFlagCount++
        }
        return
      }
      if (cell.flagged) { return }
      if (cell.mine) {
        cell.revealed = true
        this.msBoomIndex = idx
        this.msStatus = 'lose'
        // 揭晓所有雷
        this.msBoard.forEach(b => { if (b.mine) { b.revealed = true } })
        if (this.msTimer) { clearInterval(this.msTimer); this.msTimer = null }
        return
      }
      this.msRevealFlood(idx)
      this.msCheckWin()
    },
    msRevealFlood (startIdx) {
      const queue = [startIdx]
      const visited = {}
      while (queue.length) {
        const idx = queue.shift()
        if (visited[idx]) { continue }
        visited[idx] = true
        const cell = this.msBoard[idx]
        if (cell.revealed || cell.flagged) { continue }
        cell.revealed = true
        if (cell.adjacent === 0 && !cell.mine) {
          this.msNeighbors(idx).forEach(n => {
            if (!visited[n]) { queue.push(n) }
          })
        }
      }
    },
    msCheckWin () {
      const safeTotal = this.msRows * this.msCols - this.msMineTotal
      const revealedCount = this.msBoard.filter(c => c.revealed).length
      if (revealedCount === safeTotal) {
        this.msStatus = 'win'
        this.msBoard.forEach(c => { if (c.mine && !c.flagged) { c.flagged = true } })
        if (this.msTimer) { clearInterval(this.msTimer); this.msTimer = null }
      }
    },

    /* ================= 工具 ================= */
    randInt (min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min
    }
  }
}
</script>

<style scoped>
/* =====================================================
 * 潮玩天地：粉紫渐变页面 + 深紫可收缩侧边栏
 * ===================================================== */
.tt-root {
  height: 100vh;
  padding-top: 60px; /* 为固定头部导航栏让位 */
  box-sizing: border-box;
  overflow: hidden;
  font-family: "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  color: #6b4a5a;
  background:
    radial-gradient(1200px 500px at 10% -10%, rgba(255, 183, 214, 0.55), transparent 60%),
    radial-gradient(1000px 460px at 110% 10%, rgba(255, 214, 231, 0.65), transparent 55%),
    linear-gradient(165deg, #fff5fa 0%, #ffeaf3 45%, #ffe3ef 100%);
}

.tt-layout {
  display: flex;
  height: 100%;
}

/* ===== 左侧侧边栏（可收缩） ===== */
.tt-side {
  position: relative;
  flex-shrink: 0;
  width: 224px;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, #4a2268 0%, #2c1150 55%, #1a0830 100%);
  box-shadow: 4px 0 18px rgba(42, 16, 64, 0.3);
  transition: width 0.3s ease;
  overflow: hidden;
  z-index: 5;
}
.tt-side.collapsed {
  width: 64px;
}

.tt-collapse-btn {
  position: absolute;
  top: 12px;
  right: 10px;
  z-index: 6;
  width: 24px;
  height: 24px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  color: #ffd6f0;
  font-size: 10px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease;
}
.tt-collapse-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}
.tt-side.collapsed .tt-collapse-btn {
  right: 20px; /* 收起后居中 */
}
.tt-collapse-arrow {
  transition: transform 0.3s ease;
}

/* ----- 上部：标题 + 账号 ----- */
.tt-side-top {
  padding: 34px 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.tt-side.collapsed .tt-side-top {
  padding: 34px 12px 16px;
  align-items: center;
}
.tt-brand {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tt-brand-icon {
  font-size: 24px;
  filter: drop-shadow(0 0 8px rgba(255, 140, 220, 0.6));
}
.tt-brand-name {
  font-family: '爱心字体', sans-serif;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 2px;
  background: linear-gradient(135deg, #ffd6f0, #ff9ed8, #d4a5ff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.tt-user {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.tt-avatar {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #ff9ec5, #b07cff);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.25);
  overflow: hidden;
}
.tt-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.tt-user-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 1px;
}
.tt-user-name {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tt-user-no {
  font-size: 11px;
  color: rgba(255, 214, 240, 0.55);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* ----- 星裙币小卡片 ----- */
.tt-coin-card {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 217, 168, 0.18), rgba(255, 255, 255, 0.06));
  border: 1px solid rgba(255, 217, 168, 0.35);
  box-shadow: inset 0 0 12px rgba(255, 217, 168, 0.12);
}
.tt-side.collapsed .tt-coin-card {
  justify-content: center;
  padding: 9px 0;
}
.tt-coin-ico {
  font-size: 16px;
  flex-shrink: 0;
}
.tt-coin-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 233, 200, 0.8);
  white-space: nowrap;
}
.tt-coin-num {
  margin-left: auto;
  font-size: 15px;
  font-weight: 800;
  color: #ffd9a8;
  white-space: nowrap;
}
.tt-side.collapsed .tt-coin-num {
  margin-left: 0;
}

/* ----- 中部：游戏菜单 ----- */
.tt-side-mid {
  flex: 1;
  overflow-y: auto;
  padding: 14px 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tt-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: rgba(255, 214, 240, 0.85);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
  white-space: nowrap;
}
.tt-menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
.tt-menu-item.active {
  background: linear-gradient(135deg, #ff8fc7, #ff5fa0);
  color: #fff;
  box-shadow: 0 4px 14px rgba(255, 95, 160, 0.45);
  transform: translateX(3px);
}
.tt-side.collapsed .tt-menu-item {
  justify-content: center;
  padding: 11px 0;
}
.tt-side.collapsed .tt-menu-item.active {
  transform: none;
}
.tt-menu-ico {
  font-size: 18px;
  flex-shrink: 0;
}

/* ----- 下部：页脚 · 设置（占位） ----- */
.tt-side-foot {
  padding: 12px 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.tt-settings-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 214, 240, 0.8);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}
.tt-settings-btn:hover {
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}
.tt-side.collapsed .tt-settings-btn {
  justify-content: center;
  padding: 10px 0;
}
.tt-settings-ico {
  font-size: 17px;
}

/* ===== 主内容区 ===== */
.tt-main {
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 24px 28px 40px;
  box-sizing: border-box;
  position: relative;
}

/* ---------- 面板 ---------- */
.mg-panel {
  max-width: 860px;
  margin: 0 auto;
  padding: 26px 30px 30px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 10px 34px rgba(255, 158, 198, 0.25);
  border: 1px solid rgba(255, 190, 216, 0.5);
  backdrop-filter: blur(4px);
}

/* ---------- y7x7 原版游戏 iframe：绝对定位铺满 tt-main 全部空间 ---------- */
.mg-panel-iframe {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  max-width: none;
  width: auto;
  border-radius: 0;
  border: 0;
  box-shadow: none;
}
.mg-game-frame {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
  background: #fff;
}
.mg-panel-head {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 2px dashed #ffd9e8;
}
.mg-panel-ico {
  font-size: 34px;
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  background: linear-gradient(135deg, #ffe3f0, #fff0f7);
  box-shadow: inset 0 0 0 2px #ffd3e4;
}
.mg-panel-title {
  font-size: 21px;
  color: #e25b96;
  font-weight: 700;
}
.mg-panel-sub {
  font-size: 13px;
  color: #c490a8;
  margin-top: 3px;
}

/* ---------- 通用按钮 ---------- */
.mg-btn {
  border: none;
  border-radius: 999px;
  padding: 11px 26px;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.mg-btn-pink {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  color: #fff;
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.35);
}
.mg-btn-pink:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(255, 111, 165, 0.45);
}
.mg-btn-pink:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.mg-btn-sm { padding: 7px 18px; font-size: 13px; }
.mg-btn-block { display: block; width: 100%; margin-top: 16px; }

/* =====================================================
 * ① 猜数字
 * ===================================================== */
.gs-levels {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}
.gs-level {
  flex: 1;
  min-width: 120px;
  padding: 10px 12px;
  border-radius: 14px;
  border: 2px solid #ffd9e8;
  background: #fff7fb;
  cursor: pointer;
  transition: all 0.25s ease;
}
.gs-level:hover { border-color: #ff9ec5; }
.gs-level.active {
  border-color: #ff6fa5;
  background: linear-gradient(135deg, #ffe3f0, #fff0f7);
  box-shadow: 0 4px 12px rgba(255, 111, 165, 0.25);
}
.gs-level-name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #e25b96;
}
.gs-level-desc {
  display: block;
  font-size: 12px;
  color: #c490a8;
  margin-top: 3px;
}
.gs-stage {
  background: #fffbfd;
  border: 1px solid #ffe0ee;
  border-radius: 18px;
  padding: 20px;
}
.gs-range {
  font-size: 15px;
  color: #b06a8a;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.gs-range b { color: #ff5fa0; font-size: 17px; }
.gs-rest {
  font-size: 13px;
  padding: 3px 12px;
  border-radius: 999px;
  background: #ffe9f3;
  color: #e25b96;
}
.gs-rest.warn {
  background: #ffe3e3;
  color: #e2574b;
  animation: mg-pulse 1s infinite;
}
@keyframes mg-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.06); }
}
.gs-form {
  display: flex;
  gap: 10px;
  margin-top: 16px;
}
.gs-input {
  flex: 1;
  min-width: 0;
  border: 2px solid #ffd3e4;
  border-radius: 999px;
  padding: 10px 20px;
  font-size: 16px;
  color: #6b4a5a;
  outline: none;
  background: #fff;
  transition: border-color 0.25s ease;
}
.gs-input:focus { border-color: #ff6fa5; }
.gs-result {
  text-align: center;
  padding: 22px 0 8px;
}
.gs-result-emoji { font-size: 52px; animation: mg-bounce 0.6s ease; }
@keyframes mg-bounce {
  0% { transform: scale(0.4); opacity: 0; }
  60% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}
.gs-result-title {
  font-size: 20px;
  font-weight: 700;
  margin-top: 10px;
  color: #e25b96;
}
.gs-result.lose .gs-result-title { color: #9a7b8a; }
.gs-result-info {
  font-size: 14px;
  color: #c490a8;
  margin-top: 6px;
}
.gs-result-info b { color: #ff5fa0; }
.gs-history { margin-top: 20px; }
.gs-history-title {
  font-size: 13px;
  color: #b06a8a;
  font-weight: 600;
  margin-bottom: 8px;
}
.gs-history-list {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 140px;
  overflow-y: auto;
  padding-right: 4px;
}
.gs-history-item {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 13px;
  background: #fff3f9;
  border: 1px solid #ffddea;
}
.gs-h-num { color: #b06a8a; }
.gs-h-tip { font-weight: 700; }
.hint-high .gs-h-tip { color: #ff7a59; }
.hint-low .gs-h-tip { color: #5aa7ff; }
.hint-hit .gs-h-tip { color: #ff5fa0; }

/* =====================================================
 * ② 石头剪刀布
 * ===================================================== */
.rps-score {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-bottom: 18px;
}
.rps-score-box {
  min-width: 88px;
  text-align: center;
  padding: 10px 8px;
  border-radius: 16px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.rps-score-num { font-size: 26px; font-weight: 800; }
.rps-score-num.pink { color: #ff6fa5; }
.rps-score-num.gray { color: #b9a3af; }
.rps-score-num.blue { color: #6aa9ff; }
.rps-score-label { display: block; font-size: 12px; color: #c490a8; margin-top: 2px; }
.rps-arena {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  padding: 22px 10px;
  background: linear-gradient(160deg, #fff0f7, #ffe9f3);
  border-radius: 20px;
  border: 1px solid #ffddea;
}
.rps-side { text-align: center; }
.rps-side-label {
  font-size: 14px;
  color: #b06a8a;
  font-weight: 600;
  margin-bottom: 8px;
}
.rps-gesture {
  width: 92px;
  height: 92px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 6px 18px rgba(255, 158, 198, 0.3);
  border: 3px solid #ffd3e4;
}
.rps-emoji { font-size: 46px; }
.rps-empty { opacity: 0.4; }
.rps-gesture.playing { animation: mg-shake 0.35s infinite; }
@keyframes mg-shake {
  0%, 100% { transform: rotate(-8deg) translateY(-2px); }
  50% { transform: rotate(8deg) translateY(2px); }
}
.rps-vs { text-align: center; min-width: 90px; }
.rps-vs-text {
  font-size: 18px;
  font-weight: 800;
  color: #c98ba6;
  letter-spacing: 2px;
}
.rps-vs-text.win { color: #ff5fa0; }
.rps-vs-text.lose { color: #7aa7f0; }
.rps-vs-text.draw { color: #b9a3af; }
.rps-choices {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-top: 20px;
}
.rps-choice {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 14px 20px;
  border-radius: 18px;
  border: 2px solid #ffd3e4;
  background: #fff;
  cursor: pointer;
  transition: all 0.25s ease;
}
.rps-choice:hover:not(:disabled) {
  transform: translateY(-4px) scale(1.04);
  border-color: #ff6fa5;
  box-shadow: 0 8px 18px rgba(255, 111, 165, 0.3);
}
.rps-choice:disabled { opacity: 0.55; cursor: not-allowed; }
.rps-choice-emoji { font-size: 34px; }
.rps-choice-name { font-size: 14px; color: #b06a8a; font-weight: 600; }

/* =====================================================
 * ③ 记忆翻牌
 * ===================================================== */
.mem-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.mem-info {
  font-size: 14px;
  color: #b06a8a;
  font-weight: 600;
}
.mem-done {
  text-align: center;
  padding: 14px;
  margin-bottom: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #ffe9f3, #fff0f7);
  border: 2px solid #ffc9df;
  animation: mg-bounce 0.5s ease;
}
.mem-done-emoji { font-size: 34px; display: block; }
.mem-done-text {
  font-size: 15px;
  color: #e25b96;
  font-weight: 600;
  margin-top: 6px;
}
.mem-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  max-width: 460px;
  margin: 0 auto;
}
.mem-card {
  position: relative;
  aspect-ratio: 1;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  perspective: 600px;
  background: transparent;
  padding: 0;
}
.mem-card-back,
.mem-card-front {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  font-size: 32px;
  transition: transform 0.4s ease, opacity 0.3s ease;
  backface-visibility: hidden;
}
.mem-card-back {
  background: linear-gradient(135deg, #ff9ec5, #ff6fa5);
  box-shadow: 0 5px 14px rgba(255, 111, 165, 0.35);
  color: rgba(255, 255, 255, 0.9);
  transform: rotateY(0deg);
}
.mem-card-front {
  background: #fff;
  border: 2px solid #ffd3e4;
  box-shadow: 0 4px 10px rgba(255, 158, 198, 0.2);
  transform: rotateY(180deg);
}
.mem-card.flipped .mem-card-back { transform: rotateY(180deg); }
.mem-card.flipped .mem-card-front { transform: rotateY(0deg); }
.mem-card.matched .mem-card-front {
  border-color: #ff8fc7;
  background: #fff0f7;
  animation: mg-pop 0.4s ease;
}
.mem-card.matched { cursor: default; }
@keyframes mg-pop {
  0% { transform: scale(0.85); }
  50% { transform: scale(1.12); }
  100% { transform: scale(1); }
}

/* =====================================================
 * ④ 井字棋
 * ===================================================== */
.ttt-bar {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 14px;
}
.ttt-mode {
  padding: 8px 20px;
  border-radius: 999px;
  border: 2px solid #ffd3e4;
  background: #fff;
  color: #b06a8a;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.ttt-mode.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
}
.ttt-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-bottom: 16px;
  min-height: 38px;
}
.ttt-status-win, .ttt-status-draw { font-size: 17px; font-weight: 800; }
.ttt-status-win.pink { color: #ff5fa0; }
.ttt-status-win.blue { color: #5aa7ff; }
.ttt-status-draw { color: #b9a3af; }
.ttt-turn { font-size: 16px; font-weight: 700; }
.ttt-turn.pink { color: #ff5fa0; }
.ttt-turn.blue { color: #5aa7ff; }
.ttt-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  max-width: 320px;
  margin: 0 auto;
}
.ttt-cell {
  aspect-ratio: 1;
  border: none;
  border-radius: 18px;
  background: #fff7fb;
  box-shadow: inset 0 0 0 2px #ffddea;
  font-size: 44px;
  font-weight: 800;
  cursor: pointer;
  transition: all 0.2s ease;
}
.ttt-cell:hover:not(:disabled) {
  background: #ffeef6;
  box-shadow: inset 0 0 0 2px #ff9ec5;
}
.ttt-cell.win {
  background: linear-gradient(135deg, #ffe3f0, #ffd6e8);
  box-shadow: inset 0 0 0 2px #ff8fc7;
  animation: mg-pop 0.4s ease;
}
.ttt-cell:disabled { cursor: not-allowed; }
.cell-x { color: #ff5fa0; text-shadow: 0 2px 8px rgba(255, 95, 160, 0.3); }
.cell-o { color: #5aa7ff; text-shadow: 0 2px 8px rgba(90, 167, 255, 0.3); }
.ttt-score {
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 18px;
  font-size: 14px;
  color: #b06a8a;
  font-weight: 600;
}

/* =====================================================
 * ⑤ 扫雷
 * ===================================================== */
.ms-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 10px 16px;
  border-radius: 14px;
  background: #fff7fb;
  border: 1px solid #ffddea;
}
.ms-mode {
  display: flex;
  gap: 8px;
}
.ms-mode-btn {
  padding: 6px 16px;
  border-radius: 999px;
  border: 2px solid #ffd3e4;
  background: #fff;
  color: #b06a8a;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.25s ease;
}
.ms-mode-btn.active {
  background: linear-gradient(135deg, #ff8fc7, #ff6fa5);
  border-color: #ff6fa5;
  color: #fff;
}
.ms-info {
  font-size: 14px;
  color: #b06a8a;
  font-weight: 700;
}
.ms-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 18px;
  background: rgba(255, 240, 247, 0.88);
  cursor: pointer;
  z-index: 5;
  backdrop-filter: blur(2px);
  animation: mg-fade 0.3s ease;
}
@keyframes mg-fade {
  from { opacity: 0; }
  to { opacity: 1; }
}
.ms-overlay-emoji { font-size: 46px; animation: mg-bounce 0.5s ease; }
.ms-overlay-text {
  font-size: 16px;
  font-weight: 700;
  color: #e25b96;
}
.ms-board {
  position: relative;
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 5px;
  max-width: 480px;
  margin: 0 auto;
  padding: 14px;
  border-radius: 18px;
  background: linear-gradient(160deg, #fff0f7, #ffe9f3);
  border: 1px solid #ffddea;
}
.ms-cell {
  aspect-ratio: 1;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  background: linear-gradient(145deg, #ff9ec5, #ff6fa5);
  box-shadow: 0 2px 5px rgba(255, 111, 165, 0.3);
  color: #fff;
  transition: transform 0.12s ease;
}
.ms-cell:hover:not(.revealed) {
  transform: translateY(-2px);
  filter: brightness(1.06);
}
.ms-cell.revealed {
  background: #fff7fb;
  box-shadow: inset 0 0 0 1px #ffddea;
  color: #6b4a5a;
  cursor: default;
}
.ms-cell.is-mine {
  background: #ffe3e3;
  box-shadow: inset 0 0 0 1px #ffb3b3;
}
.ms-cell.boom {
  animation: mg-boom 0.4s ease;
  box-shadow: 0 0 0 3px #ff7a7a;
}
@keyframes mg-boom {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.35); }
}
.ms-ico { line-height: 1; }
.ms-blank { display: block; width: 100%; height: 100%; }
.ms-num.n1 { color: #5a8dff; }
.ms-num.n2 { color: #3fb57a; }
.ms-num.n3 { color: #ff5f6d; }
.ms-num.n4 { color: #9a5fd0; }
.ms-num.n5 { color: #d05f8a; }
.ms-num.n6 { color: #35a8b5; }
.ms-num.n7 { color: #6b4a5a; }
.ms-num.n8 { color: #8a8a9a; }

/* ---------- 响应式 ---------- */
@media (max-width: 700px) {
  .tt-side { width: 64px; }
  .tt-side .tt-brand-name,
  .tt-side .tt-user-info,
  .tt-side .tt-menu-name,
  .tt-side .tt-settings-name {
    display: none !important;
  }
  .tt-side .tt-collapse-btn { display: none; }
  .tt-main { padding: 16px 12px 30px; }
  .mg-panel { padding: 18px 16px 22px; }
  .mem-grid { gap: 8px; }
  .mem-card-back, .mem-card-front { font-size: 24px; border-radius: 12px; }
  .rps-gesture { width: 72px; height: 72px; }
  .rps-emoji { font-size: 36px; }
  .ms-board { gap: 3px; padding: 10px; }
  .ms-cell { font-size: 12px; border-radius: 6px; }
  .gs-form { flex-direction: column; }
}
</style>
